// CouponsManager — Direct-booking coupon pipeline visibility.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Gift, RefreshCw, Loader2, Check, Copy, MessageCircle, Trash2, Search, Printer,
} from 'lucide-react';
import { API_URL, apiCall } from './_shared';
import LoyaltyCard from './LoyaltyCard';

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const fmtShort = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return iso; }
};

const isExpired = (item) => {
  if (item.redeemed_at) return false;
  if (!item.expires_at) return false;
  return new Date(item.expires_at) < new Date();
};

const statusOf = (item) => {
  if (item.redeemed_at) return 'redeemed';
  if (isExpired(item)) return 'expired';
  return 'active';
};

const STATUS_STYLE = {
  active: 'bg-green-100 text-green-700 border-green-200',
  redeemed: 'bg-blue-100 text-blue-700 border-blue-200',
  expired: 'bg-gray-100 text-gray-500 border-gray-200',
};

const KpiCard = ({ icon: Icon, label, value, color, testid }) => {
  const map = {
    orange: 'bg-orange-50 text-orange-600',
    green: 'bg-green-50 text-green-600',
    blue: 'bg-blue-50 text-blue-600',
    gray: 'bg-gray-50 text-gray-600',
  };
  const c = map[color] || map.orange;
  return (
    <div className="bg-white rounded-xl border p-4" data-testid={testid}>
      <div className={`inline-flex items-center gap-2 px-2 py-1 rounded ${c} text-xs uppercase tracking-wider font-semibold`}>
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
    </div>
  );
};

const CouponsManager = ({ userRole }) => {
  const [data, setData] = useState({ items: [], counts: { total: 0, active: 0, redeemed: 0, expired: 0 } });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [copiedCode, setCopiedCode] = useState('');
  const [redeeming, setRedeeming] = useState(null);
  const canRedeem = ['super_admin', 'admin'].includes(userRole);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const params = new URLSearchParams({ limit: '500' });
      if (filter !== 'all') params.append('status', filter);
      const json = await apiCall(`/api/admin/coupons?${params.toString()}`);
      setData(json);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(''), 2000);
    } catch {}
  };

  const redeem = async (code) => {
    const bookingId = window.prompt(`Mark ${code} as redeemed. Optional: Beds24 booking ID (the booking value is looked up for revenue tracking)`);
    // Empty string is ok, null means cancel.
    if (bookingId === null) return;
    const amount = window.prompt('Booking value in € (optional — leave empty to use the Beds24 price)') || '';
    setRedeeming(code);
    try {
      const params = new URLSearchParams();
      if (bookingId) params.set('booking_id', bookingId);
      if (amount && !Number.isNaN(Number(amount))) params.set('amount_eur', String(Number(amount)));
      const qs = params.toString() ? `?${params}` : '';
      await apiCall(`/api/admin/coupons/${code}/redeem${qs}`, { method: 'PATCH' });
      await load();
    } catch (e) {
      alert('Redeem failed: ' + e.message);
    } finally {
      setRedeeming(null);
    }
  };

  // Open a print-ready window with a beautiful coupon slip for reception to hand out.
  const printSlip = (item) => {
    const origin = window.location.origin;
    const qrUrl = `${API_URL}/api/coupons/${item.code}/qr.png`;
    const redeemUrl = `${origin}/redeem/${item.code}`;
    const expires = new Date(item.expires_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${item.code}</title>
      <style>
        @page { size: 105mm 74mm; margin: 0; }
        body { margin: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
        .card { width: 105mm; height: 74mm; padding: 8mm; box-sizing: border-box; background: linear-gradient(135deg,#f97316,#e11d48); color: white; position: relative; overflow: hidden; }
        .card::before { content:''; position:absolute; top:-30mm; right:-30mm; width:70mm; height:70mm; background:rgba(255,255,255,.14); border-radius:50%; }
        .brand { font-size: 10pt; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; opacity: .9; }
        .title { font-size: 20pt; font-weight: 800; margin-top: 2mm; line-height: 1.05; }
        .body { display: grid; grid-template-columns: 1fr auto; gap: 4mm; align-items: end; margin-top: 6mm; position: relative; }
        .code-box { background: white; color: #c2410c; padding: 3mm 4mm; border-radius: 3mm; text-align: center; }
        .code-label { font-size: 7pt; text-transform: uppercase; letter-spacing: 2px; color: #c2410c; }
        .code { font-family: 'Courier New', monospace; font-size: 15pt; font-weight: 800; letter-spacing: 2px; }
        .expires { font-size: 7pt; margin-top: 1mm; opacity: .85; }
        .qr { background: white; padding: 2mm; border-radius: 2mm; }
        .qr img { width: 22mm; height: 22mm; display: block; }
        .foot { position: absolute; bottom: 6mm; left: 8mm; right: 8mm; font-size: 7pt; opacity: .85; display: flex; justify-content: space-between; }
      </style></head>
      <body onload="window.print(); setTimeout(window.close, 400);">
        <div class="card">
          <div class="brand">Hotel La Rosa Negra · Ulcinj</div>
          <div class="title">${item.discount_pct}% off your next direct booking</div>
          <div class="body">
            <div>
              <div class="code-box">
                <div class="code-label">Code</div>
                <div class="code">${item.code}</div>
                <div class="expires" style="color:#c2410c">valid until ${expires}</div>
              </div>
            </div>
            <div class="qr"><img src="${qrUrl}" alt="QR"></div>
          </div>
          <div class="foot">
            <span>${redeemUrl.replace(/^https?:\/\//, '')}</span>
            <span>Scan or WhatsApp us</span>
          </div>
        </div>
      </body></html>`;
    const w = window.open('', '_blank', 'width=520,height=380');
    if (!w) { alert('Please allow pop-ups to print the coupon slip.'); return; }
    w.document.open(); w.document.write(html); w.document.close();
  };

  // Bulk print: A4 sheet with 8 slips (2 cols × 4 rows) for reception to
  // pre-print and hand out at check-out throughout the week.
  const printSheet = () => {
    const active = (data.items || []).filter((i) => statusOf(i) === 'active').slice(0, 8);
    if (active.length === 0) {
      alert('No active coupons to print.');
      return;
    }
    const origin = window.location.origin;
    const cards = active.map((item) => {
      const qrUrl = `${API_URL}/api/coupons/${item.code}/qr.png`;
      const redeemUrl = `${origin}/redeem/${item.code}`;
      const expires = new Date(item.expires_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
      return `
        <div class="slip">
          <div class="brand">Hotel La Rosa Negra · Ulcinj</div>
          <div class="title">${item.discount_pct}% off your next direct booking</div>
          <div class="body">
            <div class="code-box">
              <div class="code-label">Code</div>
              <div class="code">${item.code}</div>
              <div class="expires">valid until ${expires}</div>
            </div>
            <div class="qr"><img src="${qrUrl}" alt="QR"></div>
          </div>
          <div class="foot">
            <span>${redeemUrl.replace(/^https?:\/\//, '')}</span>
            <span>Scan or WhatsApp us</span>
          </div>
        </div>`;
    }).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Coupon sheet</title>
      <style>
        @page { size: A4 portrait; margin: 8mm; }
        body { margin: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
        .sheet { display: grid; grid-template-columns: 1fr 1fr; grid-auto-rows: 68mm; gap: 4mm; }
        .slip { padding: 6mm; box-sizing: border-box; background: linear-gradient(135deg,#f97316,#e11d48); color: white; position: relative; overflow: hidden; border-radius: 3mm; break-inside: avoid; }
        .slip::before { content:''; position:absolute; top:-24mm; right:-24mm; width:56mm; height:56mm; background:rgba(255,255,255,.14); border-radius:50%; }
        .brand { font-size: 8pt; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; opacity: .9; }
        .title { font-size: 14pt; font-weight: 800; margin-top: 2mm; line-height: 1.1; position: relative; }
        .body { display: grid; grid-template-columns: 1fr auto; gap: 3mm; align-items: end; margin-top: 4mm; position: relative; }
        .code-box { background: white; color: #c2410c; padding: 2mm 3mm; border-radius: 2mm; text-align: center; }
        .code-label { font-size: 6pt; text-transform: uppercase; letter-spacing: 2px; color: #c2410c; }
        .code { font-family: 'Courier New', monospace; font-size: 11pt; font-weight: 800; letter-spacing: 1.5px; }
        .expires { font-size: 6pt; margin-top: 1mm; color: #c2410c; opacity: .85; }
        .qr { background: white; padding: 1.5mm; border-radius: 1.5mm; }
        .qr img { width: 18mm; height: 18mm; display: block; }
        .foot { position: absolute; bottom: 3mm; left: 6mm; right: 6mm; font-size: 6pt; opacity: .85; display: flex; justify-content: space-between; }
      </style></head>
      <body onload="setTimeout(() => { window.print(); setTimeout(window.close, 500); }, 700);">
        <div class="sheet">${cards}</div>
      </body></html>`;
    const w = window.open('', '_blank', 'width=760,height=900');
    if (!w) { alert('Please allow pop-ups to print the coupon sheet.'); return; }
    w.document.open(); w.document.write(html); w.document.close();
  };

  const items = data.items || [];
  const counts = data.counts || {};

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const needle = q.trim().toLowerCase();
    return items.filter((i) =>
      (i.code || '').toLowerCase().includes(needle)
      || (i.email || '').toLowerCase().includes(needle)
      || (i.lang || '').toLowerCase().includes(needle)
      || (i.redeemed_booking_id || '').toLowerCase().includes(needle)
      || (i.client_ip || '').toLowerCase().includes(needle)
    );
  }, [items, q]);

  // Rows hoisted OUT of JSX (Babel workaround).
  const rows = filtered.map((it) => {
    const st = statusOf(it);
    const stCls = STATUS_STYLE[st] || STATUS_STYLE.active;
    return (
      <tr key={it.code} className="border-b last:border-b-0 hover:bg-gray-50" data-testid={`coupon-row-${it.code}`}>
        <td className="py-2 pr-3">
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm font-bold text-gray-900">{it.code}</span>
            <button
              onClick={() => copyCode(it.code)}
              className="text-gray-400 hover:text-orange-500"
              title="Copy code"
              data-testid={`coupon-copy-${it.code}`}
            >
              {copiedCode === it.code ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
            </button>
          </div>
          <p className="text-[11px] text-gray-400">{it.lang || 'en'} · {(it.source || 'web')}</p>
        </td>
        <td className="py-2 pr-3">
          <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold border ${stCls}`}>{st}</span>
        </td>
        <td className="py-2 pr-3 text-sm text-gray-600">{fmt(it.issued_at)}</td>
        <td className="py-2 pr-3 text-sm text-gray-600">{fmtShort(it.expires_at)}</td>
        <td className="py-2 pr-3 text-sm">
          {it.redeemed_at ? (
            <div>
              <p className="text-gray-800">{fmt(it.redeemed_at)}</p>
              <p className="text-[11px] text-gray-400">
                by {it.redeemed_by || '—'}
                {it.redeemed_booking_id ? ` · booking ${it.redeemed_booking_id}` : ''}
              </p>
            </div>
          ) : <span className="text-gray-300">—</span>}
        </td>
        <td className="py-2 pr-3">
          <div className="flex items-center gap-2">
            {st === 'active' && canRedeem && (
              <button
                onClick={() => redeem(it.code)}
                disabled={redeeming === it.code}
                className="flex items-center gap-1 bg-orange-500 hover:bg-orange-600 text-white px-2.5 py-1 rounded text-xs font-semibold disabled:opacity-50"
                data-testid={`coupon-redeem-${it.code}`}
              >
                {redeeming === it.code ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Redeem
              </button>
            )}
            <button
              onClick={() => printSlip(it)}
              className="flex items-center gap-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-2.5 py-1 rounded text-xs font-semibold"
              title="Print slip (105 × 74 mm business-card size)"
              data-testid={`coupon-print-${it.code}`}
            >
              <Printer className="w-3 h-3" /> Print
            </button>
          </div>
        </td>
      </tr>
    );
  });

  return (
    <div className="space-y-6" data-testid="coupons-manager">
      <LoyaltyCard userRole={userRole} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Gift className="w-7 h-7 text-orange-500" /> Direct-Booking Coupons
          </h2>
          <p className="text-sm text-gray-500 mt-1">Personal 10% codes issued to returning visitors — the pipeline that reclaims OTA commission.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={printSheet}
            className="flex items-center gap-2 bg-white border px-3 py-1.5 rounded text-sm hover:bg-gray-50"
            data-testid="coupons-print-sheet"
            title="Print an A4 sheet with 8 active coupon slips"
          >
            <Printer className="w-4 h-4" /> Print sheet
          </button>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 bg-white border px-3 py-1.5 rounded text-sm hover:bg-gray-50"
            data-testid="coupons-refresh"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
          </button>
        </div>
      </div>

      {err && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm" data-testid="coupons-error">{err}</div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={Gift} label="Total issued" value={counts.total ?? 0} color="orange" testid="coupons-kpi-total" />
        <KpiCard icon={MessageCircle} label="Active" value={counts.active ?? 0} color="green" testid="coupons-kpi-active" />
        <KpiCard icon={Check} label="Redeemed" value={counts.redeemed ?? 0} color="blue" testid="coupons-kpi-redeemed" />
        <KpiCard icon={Trash2} label="Expired" value={counts.expired ?? 0} color="gray" testid="coupons-kpi-expired" />
      </div>

      <div className="bg-white rounded-xl border p-4 flex flex-wrap items-center gap-3">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-3 py-2 border rounded text-sm"
          data-testid="coupons-filter"
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="redeemed">Redeemed</option>
          <option value="expired">Expired</option>
        </select>
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search code / booking ID / IP / language"
            className="flex-1 px-3 py-2 border rounded text-sm"
            data-testid="coupons-search"
          />
        </div>
        <span className="text-xs text-gray-500 ml-auto">{filtered.length} shown</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border">
          <Gift className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">No coupons yet</h3>
          <p className="text-gray-500 mt-1">Returning visitors will start getting personal codes on the site — they'll appear here.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="text-left py-2.5 pr-3 pl-4">Code</th>
                  <th className="text-left py-2.5 pr-3">Status</th>
                  <th className="text-left py-2.5 pr-3">Issued</th>
                  <th className="text-left py-2.5 pr-3">Expires</th>
                  <th className="text-left py-2.5 pr-3">Redeemed</th>
                  <th className="text-left py-2.5 pr-3"></th>
                </tr>
              </thead>
              <tbody>{rows}</tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default CouponsManager;
